import React, { useState, useEffect, useCallback } from 'react';
import './Bookmarks.css';
import { BsBookmarkFill, BsThreeDots } from 'react-icons/bs';
import { AiOutlineRetweet, AiOutlineHeart, AiFillHeart } from 'react-icons/ai';
import { FaRegComment } from 'react-icons/fa';
import { FiShare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Post from '../components/Post';
import { apiService, tokenManager, isPostIdDeleted } from '../utils/api';
import config from '../config';
import { Button } from '@mui/material';

function Bookmarks() {
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, token } = useAuth();
  const [bookmarkedPosts, setBookmarkedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // ユーザーアクション状態（一元管理）
  const [userActions, setUserActions] = useState({
    liked_posts: [],
    bookmarked_posts: [],
    retweeted_posts: [],
    loading: false,
    loaded: false
  });

  // ユーザーアクション取得関数（一元化）
  const fetchUserActions = useCallback(async () => {
    if (!isLoggedIn || userActions.loading || userActions.loaded) {
      return;
    }

    try {
      setUserActions(prev => ({ ...prev, loading: true }));
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('ユーザーアクション取得: トークンなし');
        return;
      }

      console.log('=== ユーザーアクション取得開始（Bookmarks） ===');
      
      const response = await apiService.get('/api/posts/user-actions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      
      setUserActions({
        liked_posts: data.liked_posts || [],
        bookmarked_posts: data.bookmarked_posts || [],
        retweeted_posts: data.retweeted_posts || [],
        loading: false,
        loaded: true
      });
      
      console.log('ユーザーアクション取得完了（Bookmarks）:', {
        liked: data.liked_posts?.length || 0,
        bookmarked: data.bookmarked_posts?.length || 0,
        retweeted: data.retweeted_posts?.length || 0
      });
      
      // 詳細なデバッグ情報を出力
      console.log('ユーザーアクション詳細（Bookmarks）:', {
        liked_posts: data.liked_posts,
        bookmarked_posts: data.bookmarked_posts,
        retweeted_posts: data.retweeted_posts
      });
      
    } catch (error) {
      console.error('ユーザーアクション取得エラー（Bookmarks）:', error);
      setUserActions({
        liked_posts: [],
        bookmarked_posts: [],
        retweeted_posts: [],
        loading: false,
        loaded: false
      });
    }
  }, [isLoggedIn, userActions.loading, userActions.loaded]);
  
  // APIからブックマーク投稿を取得
  useEffect(() => {
    const fetchBookmarks = async () => {
      if (!isLoggedIn) {
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        
        // トークンを明示的に取得して設定
        const currentToken = token || tokenManager.getToken();
        if (!currentToken) {
          
          navigate('/login');
          return;
        }
        
        // apiServiceを使用し、明示的にAuthorizationヘッダーを設定
        const response = await apiService.get('/api/posts/bookmarks', {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            _t: Date.now() // キャッシュ回避のためのタイムスタンプ
          }
        });
        
        if (response.data && response.data.bookmarks) {
          setBookmarkedPosts(response.data.bookmarks.filter(post => !isPostIdDeleted(post.id)));
          
        } else {
          setBookmarkedPosts([]);
        }
      } catch (err) {
        
        setError('ブックマークの取得に失敗しました。');
        
        // 401エラーの場合はログインページにリダイレクト
        if (err.response && err.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarks();
  }, [isLoggedIn, navigate, token]);

  // ユーザーアクション取得を初期化時に実行
  useEffect(() => {
    if (isLoggedIn && !userActions.loaded && !userActions.loading) {
      fetchUserActions();
    }
  }, [isLoggedIn, fetchUserActions, userActions.loaded, userActions.loading]);

  // ブックマーク更新イベントを検知するリスナーを追加
  useEffect(() => {
    const handleBookmarkUpdate = (event) => {
      const { postId, bookmarked } = event.detail;
      
      // ブックマークが削除された場合、リストから該当の投稿を削除
      if (!bookmarked) {
        setBookmarkedPosts(prevPosts => 
          prevPosts.filter(post => post.id !== postId)
        );
        
      }
    };
    
    // イベントリスナーを登録
    window.addEventListener('bookmark_update', handleBookmarkUpdate);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('bookmark_update', handleBookmarkUpdate);
    };
  }, []); // 依存配列は空で、マウント時に一度だけ実行

  // ハッシュタグクリック時のハンドラ
  const handleHashtagClick = (hashtag, event) => {
    event.stopPropagation(); // イベントバブリングを停止
    // ハッシュタグの#を除いて検索クエリに使用
    const tag = hashtag.substring(1);
    navigate(`/explore?hashtag=${tag}`);
  };

  // ブックマーク削除後のコールバック関数
  const handleBookmarkUpdate = (postId) => {
    // 投稿がブックマークから削除された場合、リストから削除
    setBookmarkedPosts(posts => posts.filter(post => post.id !== postId));
  };

  // 広告の詳細ページに移動
  const handleViewAdDetails = (targetUrl) => {
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    }
  };

  // 広告コンポーネント
  const AdvertisementPost = ({ post }) => {
    // 広告いいね数のログを出力（デバッグ用）
    
    
    return (
      <div className="bookmarks__ad-post">
        <div className="bookmarks__ad-label">PR</div>
        <Post
          key={post.id}
          id={post.id}
          displayName={post.author?.display_name || post.user?.displayName}
          username={post.author?.username || post.user?.username}
          verified={post.author?.verified || post.user?.verified || false}
          text={post.text || post.content}
          avatar={post.author?.avatar || post.user?.avatar}
          image={post.image}
          createdAt={post.created_at || post.createdAt}
          likes={post.likes_count || post.like_count || 0}
          comments={0}
          retweets={0}
          authorId={post.user_id || post.author?.id}
          isAd={true}
          adMode={true}
          isLiked={post.is_liked || post.liked}
          liked={post.is_liked || post.liked}
          onLike={() => {}}
          onRetweet={() => {}}
          onComment={() => {}}
        />
        <div className="bookmarks__ad-action">
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => handleViewAdDetails(post.targetUrl)}
            className="bookmarks__ad-button"
          >
            詳細を見る
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="bookmarks">
      <div className="bookmarks__header">
        <h2>ブックマーク</h2>
        {currentUser && <span className="bookmarks__username">@{currentUser.username}</span>}
      </div>

      {loading ? (
        <div className="bookmarks__loading">
          <p>読み込み中...</p>
        </div>
      ) : error ? (
        <div className="bookmarks__error">
          <p>{error}</p>
        </div>
      ) : bookmarkedPosts.length === 0 ? (
        <div className="bookmarks__empty">
          <BsBookmarkFill className="bookmarks__emptyIcon" />
          <h3>ブックマークはまだありません</h3>
          <p>気になるツイートをブックマークに追加して、後で簡単に見つけられるようにしましょう。</p>
        </div>
      ) : (
        <div className="bookmarks__posts">
          {bookmarkedPosts.map(post => (
            post.isAdvertisement ? (
              <AdvertisementPost key={post.id} post={post} />
            ) : (
              <Post
                key={post.id}
                id={post.original_post_id || post.id}
                displayName={post.display_name || post.author?.display_name || post.user?.displayName}
                username={post.username || post.author?.username || post.user?.username}
                verified={post.author?.verified || post.user?.verified || false}
                text={post.text || post.content}
                avatar={post.avatar || post.author?.avatar || post.user?.avatar}
                image={post.image || post.image_url}
                video={post.video || post.video_url}
                createdAt={post.created_at || post.createdAt}
                likes={post.likes_count || post.likes || 0}
                comments={post.comments_count || post.comments || 0}
                retweets={post.reposts_count || 0}
                authorId={post.user_id || post.author?.id}
                isRetweet={post.is_retweet || false}
                retweeter={post.retweeter}
                poll={post.poll}
                has_poll={post.has_poll || false}
                shifts={post.shifts}
                liked={post.is_liked || userActions.liked_posts.some(id => String(id) === String(post.original_post_id || post.id))}
                bookmarked={post.is_bookmarked || userActions.bookmarked_posts.some(id => String(id) === String(post.original_post_id || post.id))}
                retweeted={post.retweeted || userActions.retweeted_posts.some(id => String(id) === String(post.original_post_id || post.id))}
              />
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default Bookmarks; 